// /security-audit — Informe público de seguridad del ecosistema R₿L.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, CheckCircle2, ExternalLink, ArrowLeft, Flame,
  Activity, Ban, Gauge, FileCheck2, Loader2, Lock, Landmark,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { API, safeJson } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";
import { useSecurityI18n, MEASURE_KEYS } from "../i18n/security";

const SEV_COLOR = {
  low: "#9ca3af",
  medium: "#fbbf24",
  high: "#fb923c",
  critical: "#f87171",
};

const fmt = (n) => (n != null ? Number(n).toLocaleString() : "—");
const short = (a) => (a ? `${a.slice(0, 8)}…${a.slice(-6)}` : "—");

export default function SecurityAuditPage() {
  const { lang } = useLanguage();
  const t = useSecurityI18n(lang);
  const [audit, setAudit] = useState(null);
  const [events, setEvents] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/security/audit`).then(safeJson).then(setAudit).catch(() => setAudit({}));
    fetch(`${API}/security/events`).then(safeJson).then((d) => setEvents(d?.events || [])).catch(() => setEvents([]));
  }, []);

  const auditDate = audit?.last_audit
    ? new Date(audit.last_audit).toLocaleDateString(
        lang === "en" ? "en-GB" : lang === "sk" ? "sk-SK" : "es-ES",
        { day: "numeric", month: "long", year: "numeric" }
      )
    : "—";

  const stats = audit?.stats || {};

  return (
    <div className="min-h-screen bg-black text-white antialiased" data-testid="security-audit-page">
      <Navbar />
      <div style={{ height: 96 }} />

      <main className="mx-auto w-full max-w-5xl px-6 py-10 md:px-10">
        <Link
          to="/shadow/tokens"
          className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-white/45 transition-colors hover:text-emerald-300"
          data-testid="security-back-link"
        >
          <ArrowLeft className="h-3 w-3" /> {t.backToToken}
        </Link>

        {/* HERO */}
        <div className="mt-8 flex items-center gap-3">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-2xl border"
            style={{ borderColor: "rgba(34,197,94,0.5)", background: "rgba(34,197,94,0.08)" }}
          >
            <ShieldCheck className="h-6 w-6" style={{ color: "#22c55e" }} />
          </div>
          <div>
            <h1 className="text-3xl font-black tracking-tight md:text-4xl">{t.securityReport}</h1>
            <p className="mt-1 text-sm text-white/55">{t.reportSub}</p>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-white/40" data-testid="security-page-last-audit">
          {t.lastAudit}: <b className="text-white/70">{auditDate}</b>
        </p>

        {!audit ? (
          <div className="flex items-center justify-center gap-2 py-24 text-white/45">
            <Loader2 className="h-5 w-5 animate-spin" /> {t.loading}
          </div>
        ) : (
          <>
            {/* MEDIDAS */}
            <Section title={t.sectionMeasures}>
              <div className="grid gap-3 sm:grid-cols-2">
                {MEASURE_KEYS.map((key) => (
                  <div
                    key={key}
                    className="flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/[0.02] p-3.5 text-sm text-white/80"
                    data-testid={`security-measure-${key}`}
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#22c55e" }} />
                    <span>{t[key]}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* CONTRATOS Y DIRECCIONES */}
            <Section title={t.sectionContracts}>
              <div className="space-y-3">
                <AddressRow
                  icon={<FileCheck2 className="h-4 w-4 text-emerald-300" />}
                  label={t.contractToken}
                  address={audit.contract?.address}
                  extra={`${t.network}: ${audit.contract?.network || "Base L2"}`}
                  href={audit.contract?.basescan_url}
                  linkLabel={t.verifyBasescan}
                  testid="security-contract-row"
                />
                <AddressRow
                  icon={<Landmark className="h-4 w-4 text-emerald-300" />}
                  label={t.treasuryWallet}
                  address={audit.treasury?.address}
                  extra={audit.treasury?.balance_rbl != null ? `${t.treasuryBalance}: ${fmt(audit.treasury.balance_rbl)} R₿L` : null}
                  href={audit.treasury?.basescan_url}
                  linkLabel={t.verifyBasescan}
                  testid="security-treasury-row"
                />
                <AddressRow
                  icon={<Flame className="h-4 w-4 text-orange-400" />}
                  label={t.burnAddressLabel}
                  address={audit.burn?.address}
                  extra={`${t.totalBurned}: ${fmt(audit.burn?.total_burned_rbl)} R₿L`}
                  href={audit.burn?.basescan_url}
                  linkLabel={t.verifyBasescan}
                  testid="security-burn-row"
                />
                <AddressRow
                  icon={<Lock className="h-4 w-4 text-pink-300" />}
                  label={t.lpLockLabel}
                  address={t.lpLockValue}
                  href={audit.lp_lock?.url}
                  linkLabel={t.verifyPinklock}
                  testid="security-lplock-row"
                />
              </div>
            </Section>

            {/* STATS 30D */}
            <Section title={t.sectionStats}>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Stat icon={<Activity className="h-4 w-4" />} label={t.statEvents} value={fmt(stats.events_30d_total)} testid="security-stat-events" />
                <Stat icon={<Ban className="h-4 w-4" />} label={t.statReplay} value={fmt(stats.replay_attempts_blocked_30d)} testid="security-stat-replay" />
                <Stat icon={<Gauge className="h-4 w-4" />} label={t.statRateLimited} value={fmt(stats.rate_limited_30d)} testid="security-stat-ratelimit" />
                <Stat icon={<FileCheck2 className="h-4 w-4" />} label={t.statTxLogged} value={fmt(stats.tx_operations_logged)} testid="security-stat-tx" />
              </div>
            </Section>

            {/* HISTORIAL DE EVENTOS */}
            <Section title={t.sectionEvents}>
              {events == null ? (
                <Loader2 className="h-4 w-4 animate-spin text-white/40" />
              ) : events.length === 0 ? (
                <div
                  className="flex items-center gap-2 rounded-xl border p-4 text-sm"
                  style={{ borderColor: "rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.05)", color: "#22c55e" }}
                  data-testid="security-no-events"
                >
                  <CheckCircle2 className="h-4 w-4" /> {t.noEvents}
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-white/10" data-testid="security-events-table">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-white/10 text-[10px] font-black uppercase tracking-[0.18em] text-white/40">
                        <th className="px-4 py-3">{t.eventType}</th>
                        <th className="px-4 py-3">{t.eventSeverity}</th>
                        <th className="px-4 py-3">{t.eventEndpoint}</th>
                        <th className="px-4 py-3">{t.eventDate}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((e, i) => (
                        <tr key={i} className="border-b border-white/[0.05] text-white/70">
                          <td className="px-4 py-2.5 font-mono">{e.event_type}</td>
                          <td className="px-4 py-2.5">
                            <span
                              className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
                              style={{ color: SEV_COLOR[e.severity] || "#9ca3af", border: `1px solid ${SEV_COLOR[e.severity] || "#9ca3af"}40` }}
                            >
                              {e.severity}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-white/50">{e.endpoint || "—"}</td>
                          <td className="px-4 py-2.5 text-white/50">
                            {e.created_at ? new Date(e.created_at).toLocaleString() : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="mb-4 text-[11px] font-black uppercase tracking-[0.24em]" style={{ color: "#22c55e" }}>
      {title}
    </h2>
    {children}
  </section>
);

const AddressRow = ({ icon, label, address, extra, href, linkLabel, testid }) => (
  <div
    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4"
    data-testid={testid}
  >
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div>
        <p className="text-xs font-bold text-white/80">{label}</p>
        <p className="mt-0.5 font-mono text-[11px] text-white/50" title={address}>
          {address && address.startsWith("0x") ? short(address) : address}
        </p>
        {extra && <p className="mt-0.5 text-[11px] text-white/40">{extra}</p>}
      </div>
    </div>
    {href && (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.14em] text-white/70 transition-all hover:-translate-y-0.5 hover:border-emerald-400/60 hover:text-emerald-300"
      >
        {linkLabel} <ExternalLink className="h-3 w-3" />
      </a>
    )}
  </div>
);

const Stat = ({ icon, label, value, testid }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-emerald-300/80">{icon}
      <span className="text-[10px] font-black uppercase tracking-[0.16em] text-white/40">{label}</span>
    </div>
    <p className="mt-2 text-2xl font-black text-white">{value}</p>
  </div>
);
